import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import './index.css';
import i18n from './i18n';
import { installOfflineInterceptor } from './lib/offline/interceptor';
import { createQueryClient } from './lib/queryClient';
import { initSentry, SentryErrorBoundary } from './lib/sentry';
import { bootstrapTheme } from './lib/theme';
import { router } from './routes';

// Initialise Sentry first so any error from the rest of the bootstrap
// chain (theme, offline interceptor, MSW worker) gets captured. No-op
// when VITE_SENTRY_DSN is empty or under vitest.
initSentry();

// Apply the persisted theme preference before React mounts so the
// first paint matches the operator's choice (no flash of light on a
// dark-mode user). No-op when localStorage holds 'system' or is unset.
bootstrapTheme();

// F11.3 — re-route mutating requests through the offline queue when
// the browser is offline. Idempotent; the helper bails out on a
// second call.
installOfflineInterceptor();

/**
 * F11.1 — silently register the PWA service worker.
 *
 * `virtual:pwa-register` is provided by vite-plugin-pwa at build
 * time; the dynamic import is intentional so the symbol is absent
 * from dev-mode bundles (devOptions.enabled is false in the plugin
 * config). `registerType: 'autoUpdate'` in vite.config swaps a new
 * SW silently — no prompt is needed here.
 *
 * The MSW browser worker (when VITE_USE_MSW=true) and the PWA SW
 * never coexist in dev because the PWA build is skipped there.
 */
function registerServiceWorkerInProduction(): void {
  if (!import.meta.env.PROD) return;
  void import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({ immediate: true });
  });
}

/**
 * Optionally start the MSW browser worker before mounting React.
 *
 * Activated only when both:
 *   - `import.meta.env.DEV` is true (Vite dev server)
 *   - `VITE_USE_MSW=true` is set in `.env.development.local`
 *
 * The dynamic import keeps the worker code (and its handlers, plus
 * the seed data) out of the production bundle entirely — the import
 * is tree-shaken when the condition is false at build time.
 */
async function enableMockingIfRequested(): Promise<void> {
  // The check is intentionally on `VITE_USE_MSW` only, not on
  // `import.meta.env.DEV`, because the latter is unreliable when
  // Vite is configured through `vitest/config`'s `defineConfig`
  // (the test mode propagates and DEV reads false even under
  // `vite serve`). VITE_USE_MSW is opt-in via `.env.development.local`
  // and never set in production builds, so this is safe.
  if (import.meta.env.VITE_USE_MSW !== 'true') return;

  try {
    const { worker } = await import('./test/mocks/browser');
    await worker.start({
      onUnhandledRequest: 'bypass', // let static assets / Vite HMR through
      serviceWorker: {
        url: '/mockServiceWorker.js',
      },
    });
    // eslint-disable-next-line no-console
    console.info(
      '[MSW] Mock backend active. Login with any email/password to enter.',
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[MSW] Failed to start the mock worker:', err);
  }
}

const queryClient = createQueryClient();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found in index.html');

registerServiceWorkerInProduction();

void enableMockingIfRequested().then(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <SentryErrorBoundary
        fallback={
          <div className="flex min-h-screen items-center justify-center p-6 text-sm text-muted-foreground">
            Something went wrong. The error has been reported — try
            reloading the page.
          </div>
        }
      >
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            <RouterProvider router={router} />
          </I18nextProvider>
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </SentryErrorBoundary>
    </StrictMode>,
  );
});
